import { useFeatureGate } from '@statsig/react-bindings';
import { differenceInMinutes, parseISO } from 'date-fns';
import { useAtomValue } from 'jotai';
import { useAtomCallback } from 'jotai/utils';
import { useCallback, useContext, useEffect, useRef } from 'react';

import { MeasureSchemaType } from '@grow-therapy-team/seedling-components';

import { TelehealthSessionInfoData_TelehealthSessionInfo_Appointment } from '../../hooks';
import { Statsig, StatsigLoadingContext } from '../../statsig/StatsigProvider';
import { delayedEntryGate, preSessionReflectionsGate } from '../../statsig/gates';
import { recordedMoodCheckin } from '../components/forms-drawer/mood-check-in/useMoodCheckinSelectionsState';
import { useGetShouldShowReflectionTopics } from '../reflections/useGetShouldShowReflectionTopics';
import {
  VisitorDrawerState,
  doesClientRequireSafetyGuardsAtom,
  hasCompletedClientMeasuresAtom,
  visitorMeasuresAtom,
} from '../state';
import { JoinSessionCalloutWrapper as JoinSessionCallout } from './delayed-entry/JoinSessionCallout';
import { deadlineAtom } from './delayed-entry/state';
import {
  PreSessionPromptWrapper as PreSessionPrompt,
  PreSessionPromptVariant,
} from './pre-session-prompt/PreSessionPrompt';
import { usePreSessionPromptState } from './pre-session-prompt/usePreSessionPromptState';
import { useTransitionToMeetingRoom } from './useWatchForRoomToken';

const PRE_SESSION_PROMPT_THRESHOLD_MINUTES = 4;

type SessionInfoSectionWrapperProps = {
  appointment?: TelehealthSessionInfoData_TelehealthSessionInfo_Appointment | null;
  formsAreLoading?: boolean;
  hasForms?: boolean;
  openDrawer: (state: VisitorDrawerState) => void;
};

function isWithinThreshold(appointmentStartTime?: string) {
  if (!appointmentStartTime) return false;
  const isWithinThreshold =
    differenceInMinutes(parseISO(appointmentStartTime), new Date()) > PRE_SESSION_PROMPT_THRESHOLD_MINUTES;
  return isWithinThreshold;
}

function appointmentHasStarted(appointmentStartTime?: string) {
  if (!appointmentStartTime) return false;
  return Date.now() > new Date(appointmentStartTime).getTime();
}
export function SessionInfoSectionWrapper({ appointment, hasForms, ...props }: SessionInfoSectionWrapperProps) {
  const { shouldShowReflectionTopics, isLoading: isLoadingShouldShowReflectionTopics } =
    useGetShouldShowReflectionTopics();
  const isLoading = useContext(StatsigLoadingContext) || isLoadingShouldShowReflectionTopics;
  const { preSessionPrompt, setPreSessionPrompt, clearPreSessionPrompt } = usePreSessionPromptState();
  const hasClearedLocalStorageRef = useRef(false); // to prevent re-computation
  const deadline = useAtomValue(deadlineAtom);
  const hasMood = !!useAtomValue(recordedMoodCheckin);
  const { value: isPreSessionReflectionsEnabled } = useFeatureGate(preSessionReflectionsGate);

  const measures = useAtomValue(visitorMeasuresAtom) ?? [];
  const visitorMeasuresAreAllScreeners = measures.reduce(
    (prev, currMeasure) =>
      prev &&
      !!currMeasure &&
      (currMeasure.schema.type === MeasureSchemaType.GAD7_WITH_SCREENER ||
        currMeasure.schema.type === MeasureSchemaType.PHQ9_WITH_SCREENER),
    true,
  );

  const transitionToMeetingRoom = useTransitionToMeetingRoom();
  const getHasCompletedClientMeasures = useAtomCallback(useCallback((get) => get(hasCompletedClientMeasuresAtom), []));
  const getDoesClientRequireSafetyGuards = useAtomCallback(
    useCallback((get) => get(doesClientRequireSafetyGuardsAtom), []),
  );

  const joinCallback = useCallback(() => {
    transitionToMeetingRoom({
      onDone() {
        hasClearedLocalStorageRef.current = true;
        clearPreSessionPrompt();
      },
    });
  }, [clearPreSessionPrompt, transitionToMeetingRoom]);

  useEffect(
    function allowPreSessionPromptToRecompute() {
      if (!hasMood) return;

      clearPreSessionPrompt();
    },
    [clearPreSessionPrompt, hasMood],
  );

  useEffect(
    function setPreSessionPromptState() {
      if (hasClearedLocalStorageRef.current) return;
      const hasCompletedClientMeasures = getHasCompletedClientMeasures();
      const shouldShowMoodSurvey =
        !hasMood &&
        !appointmentHasStarted(appointment?.timeStart) &&
        // If the user has completed the measures, then mood will be skipped once the reflections feature is enabled
        (!isPreSessionReflectionsEnabled || !hasCompletedClientMeasures);

      const shouldShowScreener = !appointmentHasStarted(appointment?.timeStart) && visitorMeasuresAreAllScreeners;

      const shouldShowClientMeasures = (isWithinThreshold(appointment?.timeStart) || shouldShowScreener) && hasForms;

      const hasNoAppointment = !appointment?.timeStart;

      const doesClientRequireSafetyGuards = getDoesClientRequireSafetyGuards();

      // Pre-session prompt display logic:
      // 1. Show Measures OR Mood Check-in (if there is an appointment and time condition is met)
      //   a. Measures are shown if the user has forms and it is more than 4 minutes before the appointment start time
      //   b. Mood is shown if the user has not completed the mood check-in and the appointment has not started
      // 2. THEN Show Reflection Topics and vibes (if the user is authenticated and the feature gate is enabled and the user has reflection topics)
      // 3. Show Vibes if no other pre-session prompt is shown. This is the only option for clients who require safety guards, who are not eligible to fill out any forms.

      if (preSessionPrompt !== undefined) {
        // once set, always set
        return;
      } else if (hasNoAppointment || doesClientRequireSafetyGuards) {
        setPreSessionPrompt(PreSessionPromptVariant.VIBES);
      } else if (shouldShowClientMeasures) {
        setPreSessionPrompt(PreSessionPromptVariant.CLIENT_MEASURES);
      } else if (shouldShowMoodSurvey) {
        setPreSessionPrompt(PreSessionPromptVariant.MOOD_SURVEY);
      } else if (shouldShowReflectionTopics) {
        setPreSessionPrompt(PreSessionPromptVariant.REFLECTION_TOPICS);
      } else {
        setPreSessionPrompt(PreSessionPromptVariant.VIBES);
      }
    },
    [
      appointment?.timeStart,
      hasForms,
      hasMood,
      preSessionPrompt,
      setPreSessionPrompt,
      visitorMeasuresAreAllScreeners,
      getHasCompletedClientMeasures,
      isPreSessionReflectionsEnabled,
      shouldShowReflectionTopics,
      getDoesClientRequireSafetyGuards,
    ],
  );

  if (!isLoading && preSessionPrompt !== undefined) {
    return (
      <div className='flex flex-col items-center gap-y-2 p-4'>
        {Statsig.checkGate(delayedEntryGate) && deadline && (
          <JoinSessionCallout deadline={deadline} joinCallback={joinCallback} />
        )}
        <PreSessionPrompt variant={preSessionPrompt} {...props} />
      </div>
    );
  }
}
